/**
 * Inline document upload / download / delete UI for the Profile view.
 *
 * Each "document" is either an uploaded file or an external link (e.g. a
 * SharePoint page, Confluence article, forum thread). Both share the same
 * UI — a chip row — distinguished by icon and the action button.
 *
 * Documents call the API directly on user actions — they are NOT part of the
 * entity row payload, so the Editor skips ``documents`` fields entirely.
 * This keeps the data model clean: documents are siblings of the entity,
 * not columns on it.
 */

import AddLinkIcon from '@mui/icons-material/AddLink';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import LinkIcon from '@mui/icons-material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../auth/AuthContext';
import type { FieldDef } from '../../models/Models';
import { api, isLinkDocument, type DocumentMeta } from '../../services/Server';
import { formatApiError } from '../../utils/Error';

/** Mirrors the backend's http(s) allowlist for external links. */
const URL_REGEX = /^https?:\/\/\S+/i;

const HUMAN_SIZE_UNITS = ['B', 'KB', 'MB', 'GB'] as const;

const formatSize = (bytes: number | null | undefined): string => {
  if (!bytes && bytes !== 0) return '';
  let size = bytes;
  let unit = 0;
  while (size >= 1024 && unit < HUMAN_SIZE_UNITS.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size.toFixed(size >= 10 || unit === 0 ? 0 : 1)} ${HUMAN_SIZE_UNITS[unit]}`;
};

const docsQueryKey = (entityType: string, entityId: number, fieldName?: string | null) =>
  ['documents', entityType, entityId, fieldName ?? '__section__'] as const;

/**
 * Wrapper around ``useMutation`` that handles the three boilerplate concerns
 * shared by every document mutation: toast on success, toast on error,
 * invalidate the documents query.
 */
const useDocMutation = <TVars,>(
  queryKey: QueryKey,
  mutationFn: (vars: TVars) => Promise<unknown>,
  successMessage: string,
  onSuccessExtra?: () => void,
) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      toast.success(successMessage);
      qc.invalidateQueries({ queryKey });
      onSuccessExtra?.();
    },
    onError: (e) => toast.error(formatApiError(e)),
  });
};

interface CommonProps {
  entityType: string;
  entityId: number;
  field: FieldDef;
}

/** Pill row for a single document - name, size (files only), action, delete. */
const DocumentChip = ({
  doc,
  onDelete,
  canDelete,
}: {
  doc: DocumentMeta;
  onDelete: (id: number) => void;
  canDelete: boolean;
}) => {
  const isLink = isLinkDocument(doc);
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: 'center',
        px: 1.25,
        py: 0.5,
        borderRadius: 1.5,
        border: '1px solid #DEE3EA',
        bgcolor: '#FAFBFD',
        minWidth: 0,
      }}
    >
      {isLink ? (
        <LinkIcon sx={{ fontSize: 16, color: '#1F6FEB', flexShrink: 0 }} />
      ) : (
        <AttachFileIcon sx={{ fontSize: 16, color: '#5C6B7A', flexShrink: 0 }} />
      )}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="body2"
          noWrap
          title={doc.external_url || doc.filename}
          sx={{ fontWeight: 500, fontSize: '0.85rem' }}
        >
          {doc.filename}
        </Typography>
        {!isLink && doc.size_bytes != null && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {formatSize(doc.size_bytes)}
          </Typography>
        )}
        {isLink && doc.external_url && (
          <Typography
            variant="caption"
            noWrap
            sx={{ color: 'text.secondary', display: 'block', maxWidth: 320 }}
          >
            {doc.external_url}
          </Typography>
        )}
      </Box>
      <Tooltip title={isLink ? 'Open link' : 'Download'}>
        <IconButton
          size="small"
          onClick={() => api.documents.open(doc).catch((e) => toast.error(formatApiError(e)))}
        >
          {isLink ? <OpenInNewIcon sx={{ fontSize: 16 }} /> : <DownloadIcon sx={{ fontSize: 16 }} />}
        </IconButton>
      </Tooltip>
      {canDelete && (
        <Tooltip title="Delete">
          <IconButton size="small" onClick={() => onDelete(doc.id)} color="error">
            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};

/** Modal that collects a URL + optional label for an external document link. */
const AddLinkDialog = ({
  open,
  onClose,
  onSubmit,
  busy,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (url: string, label: string) => void;
  busy: boolean;
}) => {
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [touched, setTouched] = useState(false);
  const isValid = URL_REGEX.test(url.trim());
  const showError = touched && !isValid;

  const reset = () => {
    setUrl('');
    setLabel('');
    setTouched(false);
  };
  const handleClose = () => {
    if (busy) return;
    reset();
    onClose();
  };
  const handleSubmit = () => {
    setTouched(true);
    if (!isValid) return;
    onSubmit(url.trim(), label.trim());
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add a link</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="URL"
            placeholder="https://confluence.example.com/page/123"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onBlur={() => setTouched(true)}
            error={showError}
            helperText={showError ? 'Must be an http(s) URL' : ' '}
            autoFocus
            fullWidth
            disabled={busy}
          />
          <TextField
            label="Label (optional)"
            placeholder="Short, human-friendly name"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            fullWidth
            disabled={busy}
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={busy || !url.trim()}
          startIcon={busy ? <CircularProgress size={14} /> : undefined}
        >
          Save link
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Hidden ``<input type=file>`` driven by an external Upload button. */
const useFilePicker = (onPick: (file: File) => void) => {
  const ref = useRef<HTMLInputElement | null>(null);
  const trigger = () => ref.current?.click();
  const input = (
    <input
      ref={ref}
      type="file"
      style={{ display: 'none' }}
      onChange={(e) => {
        const file = e.target.files?.[0];
        if (file) onPick(file);
        e.target.value = '';
      }}
    />
  );
  return { trigger, input };
};

export const DocumentFieldDisplay = ({ entityType, entityId, field }: CommonProps) => {
  const { can } = useAuth();
  // Aligned with crud_factory: ``create`` controls add/replace, ``delete``
  // controls removal. Readers (``read`` only) see download/open buttons but
  // no management UI.
  const canCreate = can('create');
  const canDelete = can('delete');
  const key = docsQueryKey(entityType, entityId, field.name);
  const [linkOpen, setLinkOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => api.documents.list(entityType, entityId, { fieldName: field.name }),
  });
  const doc = data?.[0];

  const uploadMut = useDocMutation(
    key,
    (file: File) => api.documents.upload(entityType, entityId, file, field.name),
    'Document uploaded',
  );
  const linkMut = useDocMutation(
    key,
    ({ url, label }: { url: string; label: string }) =>
      api.documents.addLink(entityType, entityId, url, label, field.name),
    'Link saved',
    () => setLinkOpen(false),
  );
  const deleteMut = useDocMutation(
    key,
    (id: number) => api.documents.remove(id),
    'Document deleted',
  );

  const { trigger, input } = useFilePicker((file) => uploadMut.mutate(file));
  const busy = uploadMut.isPending || deleteMut.isPending || linkMut.isPending || isLoading;

  return (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
      {doc ? (
        <DocumentChip
          doc={doc}
          onDelete={(id) => deleteMut.mutate(id)}
          canDelete={canDelete}
        />
      ) : (
        <Typography variant="body2" sx={{ color: '#B0BAC5' }}>
          No document
        </Typography>
      )}
      {canCreate && (
        <>
          <Button
            size="small"
            variant="outlined"
            startIcon={busy ? <CircularProgress size={14} /> : <CloudUploadIcon sx={{ fontSize: 16 }} />}
            onClick={trigger}
            disabled={busy}
          >
            {doc ? 'Replace file' : 'Upload file'}
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddLinkIcon sx={{ fontSize: 16 }} />}
            onClick={() => setLinkOpen(true)}
            disabled={busy}
          >
            {doc ? 'Replace with link' : 'Add link'}
          </Button>
        </>
      )}
      {input}
      <AddLinkDialog
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
        onSubmit={(url, label) => linkMut.mutate({ url, label })}
        busy={linkMut.isPending}
      />
    </Stack>
  );
};

export const DocumentsSectionDisplay = ({ entityType, entityId, field }: CommonProps) => {
  const { can } = useAuth();
  const canCreate = can('create');
  const canDelete = can('delete');
  const key = docsQueryKey(entityType, entityId, null);
  const [linkOpen, setLinkOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => api.documents.list(entityType, entityId, { sectionOnly: true }),
  });
  const docs = data ?? [];

  const uploadMut = useDocMutation(
    key,
    (file: File) => api.documents.upload(entityType, entityId, file),
    'Document uploaded',
  );
  const linkMut = useDocMutation(
    key,
    ({ url, label }: { url: string; label: string }) =>
      api.documents.addLink(entityType, entityId, url, label),
    'Link saved',
    () => setLinkOpen(false),
  );
  const deleteMut = useDocMutation(
    key,
    (id: number) => api.documents.remove(id),
    'Document deleted',
  );

  const { trigger, input } = useFilePicker((file) => uploadMut.mutate(file));
  const busy = uploadMut.isPending || linkMut.isPending || isLoading;

  return (
    <Stack spacing={1.5}>
      {docs.length > 0 ? (
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          {docs.map((doc) => (
            <DocumentChip
              key={doc.id}
              doc={doc}
              onDelete={(id) => deleteMut.mutate(id)}
              canDelete={canDelete}
            />
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" sx={{ color: '#B0BAC5' }}>
          No documents in this section
        </Typography>
      )}
      {canCreate && (
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={busy ? <CircularProgress size={14} /> : <CloudUploadIcon sx={{ fontSize: 16 }} />}
            onClick={trigger}
            disabled={busy}
          >
            Add document
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddLinkIcon sx={{ fontSize: 16 }} />}
            onClick={() => setLinkOpen(true)}
            disabled={busy}
          >
            Add link
          </Button>
        </Stack>
      )}
      {input}
      <AddLinkDialog
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
        onSubmit={(url, label) => linkMut.mutate({ url, label })}
        busy={linkMut.isPending}
      />
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {field.label}
      </Typography>
    </Stack>
  );
};
